<template>
  <VueSlideToggle
  :open="!$store.state.showExpand || $store.state.menuOpen"
  tag="nav"
  aria-label="site navigation"
  ref="menu"
  :duration="500"
  id="navbarSide"
  class="col-12 col-navbar d-lg-flex align-items-start flex-column bg-dark px-0"
  :style="menuCollapsed ? 'max-width: 20px;': ''">
    <button
    @click.prevent="menuCollapsed = !menuCollapsed"
    aria-hidden="true"
    title="Toggle Menu"
    tabindex="-1"
    class="menu-collapse-toggle"
    :style="menuCollapsed ? 'opacity: 1;' : ''">
      Toggle Menu
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512" width="7" v-if="!menuCollapsed" style="margin-left: -1px;"><path fill="currentColor" d="M4.2 247.5L151 99.5c4.7-4.7 12.3-4.7 17 0l19.8 19.8c4.7 4.7 4.7 12.3 0 17L69.3 256l118.5 119.7c4.7 4.7 4.7 12.3 0 17L168 412.5c-4.7 4.7-12.3 4.7-17 0L4.2 264.5c-4.7-4.7-4.7-12.3 0-17z"></path></svg>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512" width="7" v-else><path fill="currentColor" d="M187.8 264.5L41 412.5c-4.7 4.7-12.3 4.7-17 0L4.2 392.7c-4.7-4.7-4.7-12.3 0-17L122.7 256 4.2 136.3c-4.7-4.7-4.7-12.3 0-17L24 99.5c4.7-4.7 12.3-4.7 17 0l146.8 148c4.7 4.7 4.7 12.3 0 17z"></path></svg>
    </button>
    <div class="navbar navbar-expand navbar-dark flex-grow-1 align-items-start flex-column w-100">
      <ul class="nav navbar-nav flex-column flex-grow-1 w-100">
        <li
          v-for="(navItem, idx) in navItemList"
          :key="idx"
          :class="['nav-item', navItem.name === 'Log Out' ? 'mt-auto' : '']" v-if="!navItem.meta.hasOwnProperty('childOf')">
          <router-link :tabindex="($store.state.showExpand && !$store.state.menuOpen) ? '-1' : false" tag="a" :to="navPath(navItemList[idx].path)" :class="['nav-link d-flex align-items-center', {'active': isActive(navItem)}, {'nohover': navItemList[idx].meta.noLink}, slugify(`nav-${navItem.name}`)]">
            <span v-html="navItem.meta.icon" class="d-flex align-items-center mr-1 pr-2" v-if="navItem.meta.icon"></span>
            {{ navItem.displayName ? navItem.displayName : (navItem.name === 'Horses' ? (loggedInUser === 'Member') ? 'My Horses' : navItem.name : navItem.name) }}
          </router-link>
          <ul v-if="getChildren(navItemList[idx])" class="pl-0">
            <li v-for="(navItemChild, childIdx) in getChildren(navItemList[idx])" :key="childIdx" class="nav-item">
              <router-link :tabindex="($store.state.showExpand && !$store.state.menuOpen) ? '-1' : false" tag="a" :to="navPath(getChildren(navItemList[idx])[childIdx].path)" :class="['nav-link', {'active': isActive(getChildren(navItemList[idx])[childIdx])}]">{{ getChildrenDisplayName(navItemList[idx], childIdx) }}</router-link>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </VueSlideToggle>
</template>

<script>
import { VueSlideToggle } from 'vue-slide-toggle'
import axios from 'axios'
import useUserRole from '@/composables/useUserRole'
import { setActingCommittee, storeSuperSecretaryFlags } from '@/services/actingCommittee'

export default {
  components: {
    VueSlideToggle
  },
  data() {
    return {
      navItemList: [],
      loggedInUser: localStorage.role,
      menuCollapsed: false
    }
  },
  mounted() {
    if (!localStorage.bearer) {
      this.getList()
      return
    }
    axios.get(`${this.$api}/auth/me`).then((response) => {
      if (response.data.status === 'acting_committee_invalid') {
        // The committee being worked on is no longer available (e.g. stopped being financial)
        setActingCommittee(null)
        window.location.assign('/')
        return
      }
      if (response.data.status === 'maintenance') {
        localStorage.clear()
        this.$router.push('/login')
        return
      }
      if (response.data.role && response.data.role !== localStorage.role) {
        localStorage.setItem('role', response.data.role)
      }
      storeSuperSecretaryFlags(response.data)
      this.getList()
    }).catch(() => {
      this.getList()
    })
  },
  methods: {
    getList() {
      this.loggedInUser = localStorage.role
      const { isSuperSecretary } = useUserRole()
      this.navItemList = this.$router.options.routes.filter(route => route.meta.showInNavar && !(route.meta.requiresSuperSecretary && !isSuperSecretary) && (
        route.meta.requiresAuth === localStorage.role ||
        !route.meta.requiresAuth ||
        route.meta.requiresAuth.includes(localStorage.role)
      ))
    },
    navPath(path) {
      // Route definitions can carry a dynamic segment (e.g. "/committees/dashboard/:draftId?")
      // that Vue Router does not resolve when passed straight through as `to` on a nav link —
      // it navigates to the literal ":draftId" path segment instead. Strip it so nav links
      // always go to the base path.
      return path.split('/:')[0]
    },
    isActive(navItem) {
      // Compare route patterns rather than resolved paths, since navItem.path can carry
      // an optional dynamic segment (e.g. "/committees/dashboard/:draftId?") that never
      // equals the resolved "/committees/dashboard/1803" via strict string equality.
      const navItemBase = navItem.path.split('/:')[0]
      const matchedRoute = this.$route.matched[this.$route.matched.length - 1]
      const currentBase = (matchedRoute ? matchedRoute.path : this.$route.path).split('/:')[0]
      if (currentBase === navItemBase) return true
      // Child pages (e.g. Draft View/Edit) reference their parent nav item via
      // breadcrumb.link ("committees/dashboard") — match on that so the parent
      // nav link stays highlighted while browsing those child routes.
      const breadcrumb = this.$route.meta && this.$route.meta.breadcrumb
      return !!breadcrumb && breadcrumb.some(item => item.link && `/${item.link}` === navItemBase)
    },
    getChildren(currentItem) {
      // eslint-disable-next-line
      const foundItems = this.navItemList.filter(navItem => navItem.meta.childOf === currentItem.name)
      return foundItems === undefined ? false : foundItems
    },
    slugify(text) {
      return text.toString().toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '')
    },
    getChildrenDisplayName(currentItem, index) {
      const children = this.getChildren(currentItem)[index]
      return children.displayName ? children.displayName : children.name
    }
  }
}
</script>

<style lang="scss" scoped>
.col-navbar {

  @media (min-width: 768px) {
    flex: 0 1 220px;
    max-width: 220px;
  }

  @media (min-width: 1024px) {
    height: auto !important;
    overflow: visible !important;
    transition-property: all !important;

    .navbar-expand {
      overflow: hidden;
    }
  }

  @media (max-width: 1023px) {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 9999;
    min-height: 0;
    max-width: 220px !important;
  }

  @media (max-width: 768px) {
    max-width: 100% !important;
  }
}

.navbar .nav-link {
  font-size: 0.9375rem;
  white-space: initial !important;
}
</style>



// WEBPACK FOOTER //
// src/components/NavbarSide.vue